import { useMemo } from "react";

import {
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

import classNames from "../../../utils/classNames";
import { IPart } from "../../../models/Part";

const EBAY_BASE = "https://www.ebay.co.uk/sch/i.html";

/** Build an eBay UK sold-listings search URL for this part. */
export function buildEbayResearchUrl(
  part: IPart,
  options: { soldOnly?: boolean } = { soldOnly: true }
) {
  const v = part.vehicle ?? null;
  const partLabel =
    part.partNumber?.trim() ||
    part.category?.name?.trim() ||
    "";

  const tokens = [
    v?.dvlaYearOfManufacture ?? null,
    v?.combinedMake ?? null,
    v?.combinedModel ?? null,
    partLabel,
  ]
    .map((t) => (t == null ? "" : String(t).trim()))
    .filter((t) => t.length > 0);

  // Fall back to the part title when we couldn't build a useful query
  // from structured fields.
  const query = tokens.length > 0 ? tokens.join(" ") : part.title?.trim() ?? "";

  const params = new URLSearchParams();
  params.set("_nkw", query);
  if (options.soldOnly) {
    params.set("LH_Sold", "1");
    params.set("LH_Complete", "1");
  }
  // Bias towards the Vehicle Parts & Accessories category for cleaner
  // results (eBay UK category id `131090`).
  params.set("_sacat", "131090");

  return `${EBAY_BASE}?${params.toString()}`;
}

interface EbayResearchButtonProps {
  part: IPart;
  /** Compact icon-only variant — useful in tight rows. */
  iconOnly?: boolean;
  /** Visual size. */
  size?: "xs" | "sm";
  className?: string;
}

export default function EbayResearchButton({
  part,
  iconOnly = false,
  size = "sm",
  className,
}: EbayResearchButtonProps) {
  const url = useMemo(() => buildEbayResearchUrl(part), [part]);
  const label = "Research on eBay";

  const sizeClasses =
    size === "xs"
      ? "px-1.5 py-0.5 text-[11px] gap-1"
      : "px-2 py-1 text-xs gap-1.5";

  if (iconOnly) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title={label}
        aria-label={label}
        className={classNames(
          "inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 shadow-sm transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700",
          className
        )}
      >
        <MagnifyingGlassIcon className="h-3.5 w-3.5" aria-hidden="true" />
      </a>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title="Open sold + completed eBay listings for similar parts"
      className={classNames(
        "inline-flex items-center rounded-md border border-gray-200 bg-white font-medium text-gray-700 shadow-sm transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700",
        sizeClasses,
        className
      )}
    >
      <MagnifyingGlassIcon className="h-3.5 w-3.5" aria-hidden="true" />
      <span>{label}</span>
      <ArrowTopRightOnSquareIcon className="h-3 w-3 opacity-70" aria-hidden="true" />
    </a>
  );
}
